import { useMutation as useConnectMutation } from '@connectrpc/connect-query'
import { useMutation } from '@tanstack/react-query'

import type { Photo } from '@jotta/grpc-connect-openapi/photo'
import { PatchPhotoRequest_StateChange } from '@jotta/grpc-connect-openapi/photo'
import { patchPhoto as patchPhotoDescriptor } from '@jotta/grpc-connect-openapi/photoQuery'
import { queryClient } from '@jotta/query'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import { usePhotoStore } from '../store/PhotoContext'

import type { CollectionRef } from './useCollection'
import { photoInCollectionQueryKey } from './usePhoto'

export function usePatchPhotos(options: {
  collectionRef: CollectionRef
  onSuccess: () => void
}) {
  const { mutateAsync: patchPhoto } = useConnectMutation(patchPhotoDescriptor)
  const photoStore = usePhotoStore()
  return useMutation({
    ...options,
    mutationFn: ({
      photos,
      stateChange,
    }: {
      photos: Pick<Photo, 'idDeprecated' | 'ref'>[]
      stateChange: PatchPhotoRequest_StateChange
    }) =>
      Promise.all(
        photos.map(async ({ ref, idDeprecated }) => {
          await patchPhoto({
            ref,
            stateChange,
          })
          await queryClient.invalidateQueries({
            queryKey: photoInCollectionQueryKey(
              options.collectionRef,
              idDeprecated,
            ),
          })
          switch (stateChange) {
            case PatchPhotoRequest_StateChange.HIDE:
              await photoStore.timeline.hide([idDeprecated])
              break
            case PatchPhotoRequest_StateChange.UNHIDE:
              await photoStore.timeline.unhide([idDeprecated])
              break
            case PatchPhotoRequest_StateChange.UNKNOWN:
              break
            default:
              exhaustiveGuard(stateChange)
          }
        }),
      ),
  })
}
